import { SurveyDetails } from "data/slices/surveylist/interface";
import i18n from "i18n";
import keys from "locale/keys";
import { SURVEY_DATE_FORMAT } from "utils/constants";
import { formatDate, getStringWithDefaultValue } from "utils/Utils";
import {
  SurveyItemProps,
  SurveySetProps,
  SurveySortTab,
} from "widgets/dashboard/surveys/interface";

const isProd = process.env.REACT_APP_ENV === "prod";

//TODO change to generic table
export const surveysMapper = (
  list: SurveyDetails[],
  isCompletedSurveys: boolean = false,
  isError: boolean = false,
  totalCount: number = 0,
): SurveySetProps => {
  const hasSurveys = !isError && list && list.length > 0;

  var mappedItems: SurveyItemProps[] = [];
  if (hasSurveys) {
    list?.forEach((item) => {
      const quotas = item?.MasterSurvay?.quotas;
      const totalQuota = Array.isArray(quotas)
        ? quotas.find((q) => q.survey_quota_type === "Total")
        : undefined;
      mappedItems.push({
        surveyName: item.MasterSurvay.surveyName,
        surveyNumber: item.MasterSurvay.surveyNumber,
        category: getStringWithDefaultValue(item.MasterSurvay.category),
        timeLeft: formatDate(item.MasterSurvay.endDate, SURVEY_DATE_FORMAT),
        timeToComplete: `${item.MasterSurvay.lengthOfInterview} mins`,
        points: Math.trunc(item.MasterSurvay.reward),
        conversion: item.MasterSurvay.conversion,
        score: item.score,
        perClick: item.MasterSurvay.revenue_per_click || 0,
        totalQuotas: totalQuota?.number_of_respondents || 0,
        quotaType: totalQuota?.survey_quota_type || "",
        totalRemaining: item.MasterSurvay.total_remaining || 0,
        isCompletedSurveys,
        completedDate: formatDate(item.updatedAt, SURVEY_DATE_FORMAT),
        url: item.MasterSurvay.url,
        providerKey: item.MasterSurvay.provider.appKey,
        id: item.id,
        status: getStringWithDefaultValue(item.status),
      });
    });
  }

  return {
    title: isCompletedSurveys
      ? i18n.t(keys.completed_surveys)
      : i18n.t(keys.new_surveys),
    isCompletedSurveys: isCompletedSurveys,
    statusText: getStatus(hasSurveys, totalCount, isCompletedSurveys),
    headerItems: getHeaders(isCompletedSurveys),
    surveyItems: mappedItems,
    totalCount: totalCount,
  };
};

const getStatus = (
  hasSurveys: boolean,
  length: number,
  isCompletedSurveys: boolean,
) => {
  if (!hasSurveys) {
    return "";
  }
  if (isCompletedSurveys) {
    return `${length} ${i18n.t(keys.completed)}`;
  } else {
    return `${length} ${i18n.t(keys.to_complete)}`;
  }
};

const getMinutes = (item: SurveyItemProps): number =>
  parseInt(item.timeToComplete || "", 10) || 0;

//picks the survey to feature in the "good match" popup: highest score first,
//falling back to highest points when scores are tied (e.g. not yet populated)
export const getBestMatchSurvey = (
  items: SurveyItemProps[],
): SurveyItemProps | undefined => {
  if (items.length === 0) {
    return undefined;
  }
  return [...items].sort((a, b) => {
    const scoreDiff = (b.score ?? 0) - (a.score ?? 0);
    return scoreDiff !== 0 ? scoreDiff : b.points - a.points;
  })[0];
};

//sorts the currently fetched page of surveys per the selected tab; Best Match
//and Survey Profiler keep the order returned by the API (score/relevance based)
export const sortSurveyItemsByTab = (
  items: SurveyItemProps[],
  tab: SurveySortTab,
): SurveyItemProps[] => {
  const sorted = [...items];
  switch (tab) {
    case SurveySortTab.SHORT_SURVEYS:
      return sorted.sort((a, b) => getMinutes(a) - getMinutes(b));
    case SurveySortTab.MOST_POINTS:
      return sorted.sort((a, b) => b.points - a.points);
    case SurveySortTab.MAX_POINTS_PER_MIN:
      return sorted.sort((a, b) => {
        const aMinutes = getMinutes(a);
        const bMinutes = getMinutes(b);
        const aRatio = aMinutes > 0 ? a.points / aMinutes : 0;
        const bRatio = bMinutes > 0 ? b.points / bMinutes : 0;
        return bRatio - aRatio;
      });
    case SurveySortTab.BEST_MATCH:
    case SurveySortTab.SURVEY_PROFILER:
    default:
      return sorted;
  }
};

//TODO move to strings file
const getHeaders = (isCompletedSurveys: boolean) => {
  if (isCompletedSurveys) {
    return [
      i18n.t(keys.survey_number),
      i18n.t(keys.points_earned),
      i18n.t(keys.completed_date),
      i18n.t(keys.status),
    ];
  } else {
    return [
      i18n.t(keys.survey_length),
      !isProd && i18n.t(keys.conversion),
      i18n.t(keys.points),
      !isProd && i18n.t(keys.score),
      !isProd && i18n.t(keys.per_click),
      !isProd && i18n.t(keys.total_remaining),
      !isProd && i18n.t(keys.quotas),
      i18n.t(keys.end_date),
      i18n.t(keys.survey_number),
      i18n.t(keys.survey_number),
    ].filter(Boolean);
  }
};
